import { providerServices } from "./provider";
import type { SettingsRecord } from "./types";

/**
 * Where the settings record lives (D50, D51). The head memory, the provider's `owner@addr4/memory`, is the one a
 * browser with the key alone can find; it keeps only the pointer `defaultMemory`. The chosen memory keeps the full
 * record, whose `defaultMemory` names itself. A record whose `defaultMemory` names another memory is a pointer: the
 * reader follows it. A memory without a record answers its defaults with `defaultMemory` = itself, so a pointer to a
 * memory that lost its record still comes to rest there.
 */
export function headMemoryOf(provider: string): string {
  return providerServices(provider).memory;
}

/** Whether the record read from `memory` is a pointer to another memory rather than the record itself. */
export function isSettingsPointer(record: Pick<SettingsRecord, "defaultMemory">, memory: string): boolean {
  return !!record.defaultMemory && record.defaultMemory !== memory;
}

/** One write of a switch: `PUT settings` of `body` on `memory`; `replace` = the body is the whole record (`?replace=true`). */
export interface SettingsWrite {
  memory: string;
  body: Partial<SettingsRecord>;
  replace: boolean;
}

/**
 * The writes of a switch to `next`: the full record to `next` with `defaultMemory` = `next`, then the pointer alone
 * to the head (one write when `next` is the head itself). Both replace what the memory holds: a plain `PUT` merges
 * into the stored record, so the head would keep the old record around its pointer. The order matters: the pointer
 * moves only once the record is there, so a reader that follows it always finds a full record.
 */
export function settingsWrites(record: SettingsRecord, head: string, next: string): SettingsWrite[] {
  const full: SettingsRecord = { ...record, defaultMemory: next };
  const writes: SettingsWrite[] = [{ memory: next, body: full, replace: true }];
  if (next !== head) writes.push({ memory: head, body: { defaultMemory: next }, replace: true });
  return writes;
}

/** Where a reader lands: the memory whose record it keeps, the record, and the memories it read on the way. */
export interface SettingsLocation {
  memory: string;
  record: SettingsRecord;
  hops: string[];
}

/**
 * Follows the pointer from the head to the chosen memory: reads the head's record; while it names another memory,
 * reads that one. A memory read twice ends the walk at the last record (a pointer loop cannot occur with the writes
 * above, but a reader never spins). The record returned names the memory it came from.
 */
export async function followSettings(
  head: string,
  read: (memory: string) => Promise<SettingsRecord>,
  maxHops = 4,
): Promise<SettingsLocation> {
  const hops: string[] = [];
  let memory = head;
  while (true) {
    hops.push(memory);
    const record = await read(memory);
    const next = record.defaultMemory;
    if (!isSettingsPointer(record, memory) || hops.includes(next) || hops.length > maxHops)
      return { memory, record: { ...record, defaultMemory: memory }, hops };
    memory = next;
  }
}
